import { Input, Select } from "antd";
import { AntDModal } from "../../../components/antDmodal";
import { FaSearch } from "react-icons/fa";
import { useState, useMemo } from "react";
import QuestionIcon from "../../../icons/questions.svg";
import { getExchangeInfo, marketLogoMap } from "../../../helper";

interface StockSearchModalProps {
  isModalOpen: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filteredStocks: any[];
  handleSelectStock: (stock: any) => void;
  setIsModalOpen: (value: boolean) => void;
  callCompareFunction: Function;
  loading?: boolean;
}

export const StockSearchModal: React.FC<StockSearchModalProps> = ({
  isModalOpen,
  searchQuery,
  setSearchQuery,
  filteredStocks,
  handleSelectStock,
  setIsModalOpen,
  callCompareFunction,
  loading = false,
}) => {
  const [selectedStock, setSelectedStock] = useState<null | any>([]);
  const [selectedExchange, setSelectedExchange] = useState<string>("all");

  const stockExchanges = useMemo(() => {
    const exchanges = new Set<string>();
    filteredStocks?.forEach((stock) => {
      if (stock.market_name) {
        exchanges.add(stock.market_name);
      }
    });
    return Array.from(exchanges).sort();
  }, [filteredStocks]);

  const displayedStocks = useMemo(() => {
    if (selectedExchange === "all") {
      return filteredStocks;
    }
    return filteredStocks?.filter(
      (stock) => stock.market_name === selectedExchange,
    );
  }, [filteredStocks, selectedExchange]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSearchQuery("");
    setSelectedExchange("all");
  };

  return (
    <AntDModal
      title="Search for Stocks"
      open={isModalOpen}
      onCancel={handleModalClose}
      okText="Confirm"
      footer={false}
      loading={loading}
      wrapClassName="vi-ai-modal"
    >
      {/* flex column, capped to the modal's own max-height (set in
          aiscreen.theme.css) — chips/search stay a fixed size and only
          the stock table below grows/scrolls, same fix as Compare's
          stockSelectorModal.tsx. */}
      <div className="flex h-full min-h-0 flex-col">
        <h1 className="flex-shrink-0 text-sm font-normal text-[#667085]">
          Add something about the stocks that can be searched
        </h1>
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <div className="mb-3 flex-shrink-0">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedExchange("all")}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${
                    selectedExchange === "all"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }
                `}
              >
                All Exchanges
              </button>
              {stockExchanges.map((exchange) => {
                const { logo, short } = getExchangeInfo(exchange);
                return (
                  <button
                    key={exchange}
                    onClick={() => setSelectedExchange(exchange)}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                      flex items-center gap-2
                      ${
                        selectedExchange === exchange
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }
                    `}
                  >
                    {logo && (
                      <img
                        src={logo}
                        alt={short}
                        className="w-5 h-5 object-cover"
                      />
                    )}
                    {short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search Input */}
          <div className="relative flex-shrink-0">
            <FaSearch className="absolute left-3 top-3.5 z-[999] text-[#667085]" />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 rounded-md placeholder:text-[#667085]"
            />
          </div>

          {/* Stock List */}
          <div className="mt-4 flex min-h-0 flex-1 flex-col rounded-md border border-[#D0D5DD]">
            <div className="grid flex-shrink-0 grid-cols-3 gap-4 mb-2 px-2 bg-[#F9FAFB] rounded-md p-2">
              <div className="font-[500] text-black text-[16px]">Ticker</div>
              <div className="font-[500] text-black text-[16px]">
                Company Name
              </div>
              <div className="font-[500] text-black text-[16px] text-right pr-2">
                Market
              </div>
            </div>

            <div className="space-y-2 w-full min-h-0 flex-1 !overflow-auto">
              {displayedStocks?.length > 0 ? (
                <>
                  {displayedStocks.map((stock) => (
                    <div
                      key={stock.id}
                      className={`${
                        stock?.id === selectedStock?.id ? "bg-gray-300" : ""
                      } grid grid-cols-3 gap-4 px-2 py-2 hover:bg-gray-100 rounded cursor-pointer`}
                      onClick={() => {
                        setSelectedStock(stock);
                        handleSelectStock(stock);
                        callCompareFunction();
                        setSearchQuery("");
                      }}
                    >
                      <div className="flex items-center space-x-2 w-full">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center">
                          <img
                            src={`/${stock.company_logo}`}
                            alt={stock.company_logo}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span>{stock.ticker_symbol}</span>
                      </div>
                      <div className="truncate">{stock.company_name}</div>
                      <div className="w-full flex justify-end pr-2 gap-x-2">
                        {stock.short_name}
                        <img
                          src={marketLogoMap[stock?.market_name] ?? ""}
                          alt=""
                          className="w-6 h-6 object-cover"
                        />
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center !py-[20px]">
                  <img
                    src={QuestionIcon}
                    width={48}
                    height={48}
                    alt="Not found"
                  />
                  <h1 className="font-inter text-[#101828] font-[500] text-[16px] mt-4">
                    Stock Not Found
                  </h1>
                  <p className="font-inter text-[#667085] font-normal text-[14px] text-center mt-1 mb-2">
                    We were not able to find the stock you were looking for.
                    Please try searching for another term.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AntDModal>
  );
};
